import { useEffect, useState } from "react";

import { segmentEvent } from "@/utils/segmentfunctions";

const SECTION_ATTRIBUTE = "data-section";

const eventMap: Record<string, string> = {
  ingredients: "Ingredients",
  "root-causes": "Root Causes",
  lifestage: "Lifestage",
};

type StickyNavItem = { text: string; id: string };

type UseStickyNavigationArgs = {
  navRef: React.RefObject<HTMLElement | null>;
  menuRef: React.RefObject<HTMLElement | null>;
  padding: number;
  offset: number;
};

export function useStickyNavigation({ navRef, menuRef, padding, offset }: UseStickyNavigationArgs) {
  const [activeItemId, setActiveItemId] = useState<string | undefined>();
  const [visible, setVisible] = useState(false);

  const onItemClick = (item: StickyNavItem) => (e: React.MouseEvent) => {
    const activeSection = document.querySelector(`[${SECTION_ATTRIBUTE}]#${item?.id}`);
    if (!activeSection) return;

    e.preventDefault();

    const distance = window.pageYOffset + activeSection.getBoundingClientRect().top - offset;
    window.scrollTo({ top: distance, behavior: "smooth" });

    segmentEvent("Module Clicked", {
      module_location: "floating",
      text: item?.text,
      type: "button",
    });

    setActiveItemId(item?.id);
  };

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const element = document.documentElement;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setVisible(entry.isIntersecting);
        element.classList.toggle("with-sticky-nav", entry.isIntersecting);
      },
      { rootMargin: "0px 0px -100%" }
    );

    observer.observe(nav);

    return () => {
      element.classList.remove("with-sticky-nav");
      observer.unobserve(nav);
    };
  }, [navRef]);

  useEffect(() => {
    const sections = document.querySelectorAll(`[${SECTION_ATTRIBUTE}]`);
    const height = window.innerHeight;
    let threshold = 0.2;
    if (height > 500) threshold = 0.3;
    if (height > 700) threshold = 0.4;
    if (height > 900) threshold = 0.5;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement)?.id;
            setActiveItemId(id);
            const trigger = id === "ingredients" ? window.innerWidth < 768 : true;
            if (eventMap[id] && trigger) {
              segmentEvent("Interactive Section Viewed", {
                module_location: "mid_fold",
                slide_number: 1,
                slide_title: eventMap[id],
              });
            }
          }
        });
      },
      { threshold }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  useEffect(() => {
    const nav = menuRef.current;
    if (!nav || !activeItemId) return;
    const activeElement = nav.querySelector(`[href="#${activeItemId}"]`);
    if (!activeElement) return;

    const navRect = nav.getBoundingClientRect();
    const activeElementRect = activeElement.getBoundingClientRect();

    let newScrollLeft = 0;
    let shouldScroll: false | "left" | "right" = false;

    if (activeElementRect.left < navRect.left) {
      newScrollLeft = nav.scrollLeft - (navRect.left - activeElementRect.left);
      shouldScroll = "left";
    } else if (activeElementRect.right > navRect.right) {
      newScrollLeft = nav.scrollLeft + (activeElementRect.right - navRect.right);
      shouldScroll = "right";
    }

    if (shouldScroll) {
      nav.scrollTo({
        left: newScrollLeft + (shouldScroll === "left" ? -padding : padding),
        behavior: "smooth",
      });
    }
  }, [activeItemId, padding, menuRef]);

  return {
    activeItemId,
    onItemClick,
    visible,
  };
}
